import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock, MapPin, User, Presentation, Briefcase, Building2, Wrench, Mic, Trophy, Store } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatIcons = { conference: Presentation, b2b: Briefcase, exposition: Building2, atelier: Wrench, pitch: Mic, award: Trophy, marche: Store };
const formatLabels = { conference: 'Conférence', b2b: 'Rencontres B2B', exposition: 'Exposition', atelier: 'Atelier', pitch: 'Pitch', award: 'Awards', marche: 'Marché Virtuel' };

const methodologie = [
  'Une sélection rigoureuse de thématiques clés',
  'Des keynotes d\'ouverture par des figures scientifiques et techniques',
  'Des sessions d\'information et d\'échanges',
  'Une compilation des contributions au sein d\'un Livre Blanc',
  'Des recommandations opérationnelles pour les acteurs publics et privés',
];

const Programme = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/program`).then(r => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const types = [...new Set(events.map(e => e.type))];
  const filtered = filter === 'all' ? events : events.filter(e => e.type === filter);

  return (
    <div className="min-h-screen pt-16">
      {/* PAGE HERO */}
      <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="programme-hero">
        <div className="absolute inset-0 opacity-10" aria-hidden="true" />
        <div className="relative container-institutional">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Programme</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">L'Expérience URBANIK</h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">14, 15 & 16 Septembre 2026 — Palais des Congrès du Sofitel Hôtel Ivoire, Abidjan</p>
        </div>
      </section>

      {/* EVENT FORMATS */}
      <section id="conferences" className="py-20 lg:py-28" data-testid="formats-section">
        <div className="container-institutional">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Formats</p>
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">7 Formats Dynamiques</h2>
            <p className="text-[#6B7C93] max-w-2xl mx-auto">Des formats variés favorisant l'interaction et la collaboration entre tous les acteurs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Presentation, title: 'Conférences & Tables rondes', desc: 'Enjeux, tendances, innovations et perspectives sectorielles.' },
              { icon: Briefcase, title: 'Rencontres B2B', desc: 'Connecter entreprises, experts et décideurs.' },
              { icon: Building2, title: "Espace d'Exposition", desc: 'Technologies, matériaux, équipements et solutions innovantes.' },
              { icon: Wrench, title: 'Ateliers techniques', desc: 'Technologies, prototypes, outils numériques et bonnes pratiques.' },
              { icon: Mic, title: 'Pitch & Reverse Pitch', desc: 'Besoins industriels vs solutions innovantes.' },
              { icon: Trophy, title: "Remise d'Awards", desc: 'Innovations et projets exemplaires récompensés.' },
              { icon: Store, title: 'Marché Virtuel', desc: 'Continuité des échanges et ventes en ligne.' },
            ].map((fmt, i) => (
              <div key={fmt.title} className="p-6 bg-white border border-[#E1E8F0] rounded-lg hover:shadow-md transition-shadow" data-testid={`format-card-${i}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#F5A623]/10 flex items-center justify-center flex-shrink-0"><span className="text-sm font-bold text-[#F5A623]">{String(i + 1).padStart(2, '0')}</span></div>
                  <fmt.icon className="w-5 h-5 text-[#1E3A5F]" />
                </div>
                <h3 className="text-base font-bold text-[#1E3A5F] mb-2">{fmt.title}</h3>
                <p className="text-sm text-[#6B7C93] leading-relaxed">{fmt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AGENDA */}
      <section id="ateliers" className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="agenda-section">
        <div className="container-institutional">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-3">Agenda</p>
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Sessions Programmées</h2>
          </div>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-8 flex-wrap justify-center">
            <button onClick={() => setFilter('all')} className={`px-5 py-2 text-sm rounded-full transition-colors ${filter === 'all' ? 'bg-[#1E3A5F] text-white' : 'bg-white text-[#4A5568] border border-[#E1E8F0] hover:bg-[#E1E8F0]'}`} data-testid="filter-all">Tous</button>
            {types.map(t => (
              <button key={t} onClick={() => setFilter(t)} className={`px-5 py-2 text-sm rounded-full transition-colors capitalize ${filter === t ? 'bg-[#1E3A5F] text-white' : 'bg-white text-[#4A5568] border border-[#E1E8F0] hover:bg-[#E1E8F0]'}`} data-testid={`filter-${t}`}>
                {formatLabels[t] || t}
              </button>
            ))}
          </div>
          {loading ? <p className="text-center text-[#94A3B8] py-12">Chargement...</p> : filtered.length === 0 ? (
            <p className="text-center text-[#94A3B8] py-12">Aucune session programmée pour le moment.</p>
          ) : (
            <div className="space-y-4 max-w-3xl mx-auto">
              {filtered.map(ev => {
                const Icon = formatIcons[ev.type] || Presentation;
                return (
                  <div key={ev.id} className="bg-white border border-[#E1E8F0] rounded-lg p-6 hover:shadow-md transition-shadow" data-testid={`agenda-card-${ev.id}`}>
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-lg bg-[#1E3A5F]/5 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-[#1E3A5F]" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="text-xs font-semibold uppercase tracking-wider text-[#F5A623]">{formatLabels[ev.type] || ev.type}</span>
                        </div>
                        <h3 className="text-base font-bold text-[#1E3A5F] mb-1">{ev.title}</h3>
                        <p className="text-sm text-[#6B7C93] mb-3">{ev.description}</p>
                        <div className="flex items-center gap-4 text-xs text-[#94A3B8] flex-wrap">
                          {ev.time && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{ev.time}</span>}
                          {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.location}</span>}
                          {ev.speaker && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{ev.speaker}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* MÉTHODOLOGIE */}
      <section id="networking" className="py-20 lg:py-28" data-testid="methodologie-section">
        <div className="container-institutional">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Méthodologie</p>
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Notre Approche</h2>
              <div className="w-16 h-1 bg-[#F5A623] rounded-full mb-8" />
              <ul className="space-y-4">
                {methodologie.map((item, i) => (
                  <li key={`method-${i}`} className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-[#F5A623]/10 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-[#F5A623]">{i + 1}</span>
                    <span className="text-sm text-[#4A5568] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-[#1E3A5F] to-[#152A45] flex items-center justify-center p-10" aria-hidden="true">
              {/* Decorative institutional composition — geometric, brand-aligned, no stock people */}
              <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px, 60px 60px' }} />
              <div className="absolute -right-12 -bottom-12 w-56 h-56 rounded-full bg-[#F5A623]/15 blur-2xl" />
              <div className="absolute -left-8 -top-8 w-40 h-40 rounded-full bg-[#F5A623]/10 blur-xl" />
              <div className="relative grid grid-cols-3 gap-3 w-full max-w-xs">
                {methodologie.slice(0, 6).map((_, i) => (
                  <div key={`tile-${i}`} className={`aspect-square rounded-lg backdrop-blur-sm border border-white/15 flex items-center justify-center text-white font-bold text-lg ${i % 2 === 0 ? 'bg-white/10' : 'bg-[#F5A623]/20'}`}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="programme-cta">
        <div className="container-institutional text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Participez au programme</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Inscrivez-vous pour assister aux conférences, ateliers et networking.</p>
          <Link to="/participer" className="btn-primary text-base px-8 py-4">S'inscrire <ArrowRight className="w-5 h-5 ml-2" /></Link>
        </div>
      </section>
    </div>
  );
};

export default Programme;
